import { BOX_FOLDER_ID_REGEX, BOX_FOLDER_URL_BASE } from 'src/constants';

/**
 * Human-facing Box link for a bare folder id.
 *
 * Note there is deliberately no composite-id construction or parsing here. Bender's
 * `syncTargetId` (`box:<folderId>`) is only ever received from the API and handed
 * straight back in a request path, so the frontend never needs to build or split one.
 */
export const boxFolderUrl = (targetId: string): string => `${BOX_FOLDER_URL_BASE}${targetId}`;

const BOX_FOLDER_URL_PATH = /(?:^|\/)folder\/(\d{1,20})(?:\/|$)/;

/**
 * Canonicalizes user input into a bare numeric Box folder id, accepting either a bare
 * id or a Box folder URL.
 *
 * This deliberately mirrors Bender's `ParseTargetInputPipe` exactly — `https:` only,
 * host `app.box.com` or a subdomain of it, path segment `/folder/<digits>`. Accepting
 * anything looser here means the client would happily submit input the server then
 * rejects with a 400.
 */
export const parseBoxFolderInput = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  if (BOX_FOLDER_ID_REGEX.test(trimmed)) {
    return trimmed;
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return undefined;
  }

  if (url.protocol !== 'https:') {
    return undefined;
  }
  if (url.hostname !== 'app.box.com' && !url.hostname.endsWith('.app.box.com')) {
    return undefined;
  }

  return BOX_FOLDER_URL_PATH.exec(url.pathname)?.[1];
};
